/**
 * LiveFeedbackPanel.tsx — Panneau des barres de progression affichées pendant
 * la simulation vocale (à droite de la scène d'appel).
 *
 * Spec validée par Fabien + Valérie (maquette 21/07/26 — cf.
 * docs/mockups/simulation-feedback-progressif.html) :
 *  - Titre "Barres de progression" (renommé depuis "Feedback progressif")
 *  - Ouvert par défaut au lancement de la simulation
 *  - Bouton "Masquer" clair (icône œil + label) en haut à droite du panneau
 *  - Une fois fermé : poignée verticale réduite avec juste une flèche `<`
 *    (pas de texte — l'user a déjà vu le panneau ouvert au début)
 *  - Chaque critère : nom à gauche + barre horizontale avec fill dégradé
 *    rouge → orange → jaune → vert selon la valeur (0-10)
 *  - Barre grise si valeur absente (critère en attente d'évaluation)
 *  - Pas de score chiffré affiché — juste la position sur la barre colorée
 *  - Transition CSS 500ms sur les changements de valeur
 *
 * Composant AGNOSTIQUE DU TRANSPORT : accepte juste `values` en prop
 * (Record<criterionKey, 0-10>). Le hook `useLiveFeedbackStream` (à venir
 * une fois le back Sylvain/moi côté BizCoach en place) alimentera ces
 * valeurs via SSE. Pour l'instant : `values` vide → toutes les barres grises.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, ChevronLeft } from "lucide-react";
import { pickScoringLabel } from "../lib/scoring-i18n";

interface Criterion {
  key: string;
  labelFr?: string | null;
  labelEn?: string | null;
  label?: string | null;
  criterionType?: string | null;
  /** Critère globalement actif (Switch admin). Défaut true si absent. */
  isActive?: boolean;
  /**
   * Flag admin (2026-07-22) : afficher ou pas dans les barres de progression
   * pendant la simulation. Défaut true si absent — comportement historique
   * (tous les critères de type score s'affichaient).
   */
  showInProgressBar?: boolean;
}

/**
 * Valeur reçue par barre : score absolu (5-10) + tendance (progression/régression).
 * Le trend est envoyé par le workflow chuchoteur (N8N) dans chaque state.criteria[key]
 * via un enum `"up" | "down" | "flat"` — schema JSON strict côté Sylvain.
 */
export interface LiveFeedbackValue {
  score: number;
  trend?: "up" | "down" | "flat";
}

interface Props {
  /** Critères d'évaluation du simulateur en cours (via accountApi.getEvaluationCriteria). */
  criteria: Criterion[];
  /** Valeurs par criterionKey (score + trend). Vide = toutes les barres grises. */
  values?: Record<string, LiveFeedbackValue>;
}

// Mapping SCORE → LARGEUR (%) — non-linéaire, validé Mariam 29/07/26 (v4).
//
// Historique du mapping :
//   - v1/v2 : linéaire (score × 10%) → score 5 = 50%, score 10 = 100%.
//   - v4 (retenue) : non-linéaire, calé sur les paliers d'apprentissage.
//     Score 5-6 = « insuffisant » (barres courtes 20-30%), score 7 = seuil
//     « correct » (grand saut à 50%), score 8-10 = « bon à excellent » (70-100%).
//     Le grand saut 6→7 (+20 points de largeur pour +1 de score) matérialise
//     visuellement le passage d'un tour raté à un tour maîtrisé.
//
// Rationale plancher ≤ 4 = pending : le workflow N8N chuchoteur pose 4 par défaut
// sur tous les critères au 1er tour (SCORE_FLOOR côté prompt système). Sans ce
// masquage, toutes les barres apparaîtraient en même temps dès que l'user parle
// = confusion. On laisse donc l'échelle utile commencer à 5.
function toPercent(value: number | undefined): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  const s = Math.round(value);
  if (s <= 4) return null; // pending (cf. rationale ci-dessus)
  if (s === 5) return 20;
  if (s === 6) return 30;
  if (s === 7) return 50;
  if (s === 8) return 70;
  if (s === 9) return 80;
  return 100; // score 10 (et clamp implicite pour valeurs > 10)
}

// Couleur SELON LE SCORE — v4 (Mariam 29/07/26 soir).
//
// Historique :
//   - v1 : dégradé linéaire rouge→vert sur toute la barre → rouge dominant.
//   - v2 : couleur par barre selon le SCORE (5=rouge, ...) — trop punitif sur 5.
//   - v3 : couleur selon le TREND (progression/régression), largeur selon score
//     → intéressant mais couleur verte possible sur score bas si progression
//     → potentiellement trompeur.
//   - v4 (retenue) : couleur selon le SCORE avec paliers larges (5-6 orange,
//     7 jaune, 8-10 vert). Pas de rouge (philo « ne pas démoraliser »).
//     Info de dynamique (progression/régression) portée par la variation de
//     largeur (la barre grandit/rétrécit visiblement entre 2 mises à jour).
//     Trend reste extrait du SSE mais n'est plus utilisé visuellement — dispo
//     pour usage futur (ex: petite flèche ↑↓ à côté du label).
function scoreColor(value: number): string {
  const s = Math.round(value);
  if (s <= 5) return "#F97316"; // orange — score 5 (insuffisant)
  if (s === 6) return "#EAB308"; // jaune — score 6 (limite)
  return "#22C55E"; // vert — scores 7, 8, 9, 10 (correct à excellent)
}

export function LiveFeedbackPanel({ criteria, values = {} }: Props) {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(true); // ouvert par défaut (spec)

  // On n'affiche que les critères de type "score" (barres 0-10).
  // Les critères booléens (objectifs finaux type meetingBooked, agreementReached)
  // sont exclus par convention — non affichés en temps réel car ne peuvent être
  // évalués qu'à la fin par le feedbackAgent. Cf. spec du 20/07/26.
  //
  // Exclus aussi :
  //   - critères désactivés (isActive === false) — cohérent avec le paramétrage admin
  //   - critères marqués "hors barre de progression" (showInProgressBar === false),
  //     flag ajouté le 22/07/26 pour laisser l'admin filtrer sans désactiver
  //     complètement le critère (utile pour le feedback final).
  const scoreCriteria = criteria.filter(
    (c) =>
      (c.criterionType ?? "score") === "score"
      && c.isActive !== false
      && c.showInProgressBar !== false,
  );

  // ─── Panneau fermé : juste la flèche `<` (36px de large) ─────────────
  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        title={t("training.progressBarsShow")}
        aria-label={t("training.progressBarsShow")}
        className="hidden lg:flex w-9 shrink-0 items-center justify-center border-l bg-white hover:bg-slate-50 transition-colors cursor-pointer"
      >
        <ChevronLeft className="h-4 w-4" style={{ color: "#384872" }} />
      </button>
    );
  }

  // ─── Panneau ouvert (320px de large en desktop) ──────────────────────
  return (
    <div className="hidden lg:flex w-80 shrink-0 flex-col gap-4 border-l bg-white p-5">
      {/* Header : titre + bouton Masquer */}
      <div className="flex items-center justify-between gap-3">
        <h3
          className="font-bold text-sm"
          style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}
        >
          {t("training.progressBars")}
        </h3>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
          title={t("training.progressBarsHide")}
        >
          <EyeOff className="h-3.5 w-3.5" />
          {t("training.progressBarsHide")}
        </button>
      </div>

      {/* Liste des critères + barres */}
      <div className="flex flex-col gap-3.5">
        {scoreCriteria.length === 0 ? (
          <p className="text-xs italic text-slate-400">
            {t("training.progressBarsNoCriteria")}
          </p>
        ) : (
          scoreCriteria.map((c) => {
            const value = values[c.key];
            const pct = toPercent(value?.score);
            const isPending = pct == null;
            const label = pickScoringLabel(c as any, i18n.language);
            return (
              <div key={c.key} className="flex flex-col gap-1.5">
                <p
                  className={`text-xs font-medium ${isPending ? "text-slate-400" : "text-slate-700"}`}
                >
                  {label}
                </p>
                <div
                  className="h-2 w-full overflow-hidden rounded-full"
                  style={{ background: isPending ? "#F3F4F6" : "#E5E7EB" }}
                >
                  {/* Fill coloré : transition CSS 500ms sur width ET background-color.
                      v4 : couleur et largeur suivent toutes deux le score absolu
                      (mapping non-linéaire dans toPercent + scoreColor). Une régression
                      se voit à la barre qui rétrécit + change de couleur (7→6 = jaune 50%
                      qui devient orange 30%). Le trend N8N est extrait mais pas utilisé
                      visuellement en v4. */}
                  {!isPending && value?.score != null && (
                    <div
                      className="h-full rounded-full transition-all duration-500 ease-out"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: scoreColor(value.score),
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/**
 * Souscription SSE au feedback progressif temps réel de la session.
 *
 * Ouvre un `EventSource` sur `GET /api/sideband/live-event/:sessionId/stream`
 * (cf. src/api/live-feedback-stream.ts) et met à jour un `Record<criterionKey, { score, trend }>`
 * à chaque event reçu. Le back envoie le STATE COMPLET (criteria + flags) à chaque
 * tick — on extrait score + trend de chaque criteria pour alimenter les barres.
 * Les flags booléens (objectifs finaux) restent hors périmètre du panneau
 * (cf. spec 20/07/26).
 *
 * Nettoyage : fermeture de l'EventSource au unmount ou changement de sessionId.
 * Si sessionId est null/undefined → pas de connexion (renvoie {}).
 */
export function useLiveFeedbackValues(sessionId?: string | null): Record<string, LiveFeedbackValue> {
  const [values, setValues] = useState<Record<string, LiveFeedbackValue>>({});

  useEffect(() => {
    if (!sessionId) {
      setValues({});
      return;
    }

    // credentials par défaut = same-origin pour EventSource → cookie JWT envoyé auto.
    const eventSource = new EventSource(
      `/api/sideband/live-event/${sessionId}/stream`,
    );

    eventSource.addEventListener("live-feedback", (evt) => {
      try {
        const state = JSON.parse((evt as MessageEvent).data) as {
          criteria?: Record<string, { score: number; trend?: "up" | "down" | "flat" }>;
        };
        if (!state.criteria) return;
        // Le back envoie l'état COMPLET à chaque tick → on peut REMPLACER le state
        // au lieu de merger (garantit la cohérence, pas de "fantômes" d'un critère
        // qui aurait disparu de la grille en cours de route).
        const next: Record<string, LiveFeedbackValue> = {};
        for (const [key, entry] of Object.entries(state.criteria)) {
          if (typeof entry?.score === "number") {
            next[key] = { score: entry.score, trend: entry.trend };
          }
        }
        setValues(next);
      } catch {
        // Payload malformé — on ignore silencieusement, pas de crash de la simu.
      }
    });

    // Auto-reconnect géré nativement par EventSource — on log juste pour l'observabilité.
    eventSource.addEventListener("error", () => {
      // eslint-disable-next-line no-console
      if (eventSource.readyState === EventSource.CLOSED) {
        console.warn(`[live-feedback] EventSource fermé pour session=${sessionId}`);
      }
    });

    return () => {
      eventSource.close();
    };
  }, [sessionId]);

  return values;
}
